import { Box, Chip, Dialog, DialogContent, Stack } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { DropzoneArea } from "react-mui-dropzone";
import { SnackbarContext } from "../contexts/Snackbar";
import { isHeicFile, maxFileUploadSize, supportedFileTypes } from "../enums/Consts";
import { FileUploadContext } from "../features/appointments/context/ToggleFileUpload";
import { snackbarSeverity } from "../hooks/useSnackbar";
import ConfirmAndCancelButtons from "./buttons/ConfirmAndCancelButtons";
import SimpleAlert from "./display/SimpleAlert";
import FileUploadEditingForm from "./form/fileUpload/FileUploadEditingForm";

export default function FileUpload({
  numberOfFileLimist,
  handleClose,
  endpoint = null,
}) {
  const { t } = useTranslation();
  const context = React.useContext(FileUploadContext);
  const [selectedFile, setSelectedFile] = React.useState([]);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const [showForm, setShowForm] = React.useState(false);
  function handleSelectedFile(newFiles) {
    setSelectedFile((prevVal) => {
      const fileNames = prevVal.map((file) => file.name);
      const filteredNewFiles = newFiles.filter(
        (file) => !fileNames.includes(file.name),
      );
      return [...prevVal, ...filteredNewFiles];
    });
  }

  const handleUpload = async () => {
    if (selectedFile.length === 0) {
      handleSnackbar("SelectImage", snackbarSeverity.ERROR);
      return;
    }
    handleOpenFileForm();
    return null;
  };

  const handleDeleteFromSelectedFiles = (fileToDelete) => {
    setSelectedFile((prevVal) =>
      prevVal.filter((file) => file !== fileToDelete),
    );
  };

  function handleOpenFileForm() {
    setShowForm((prevState) => !prevState);
  }

  function getShowForm() {
    return context.getIsOpen() && showForm;
  }

  if (getShowForm()) {
    return (
      <Dialog
        open={showForm}
        onClose={() => handleOpenFileForm()}
        fullWidth={true}
        maxWidth={"md"}
      >
        <DialogContent>
          <FileUploadEditingForm
            files={selectedFile}
            back={handleOpenFileForm}
            endpoint={endpoint}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Stack spacing={2}>
      <SimpleAlert severity={"info"} message={"MaximumFileSize5MB"} />
      <DropzoneArea
        onDropRejected={(rejectedFiles) => {
          const file = rejectedFiles[0];
          if (isHeicFile(file)) {
            handleSnackbar("HeicNotSupportedSelectOther", snackbarSeverity.ERROR);
            return;
          }
          if (file && file.size > maxFileUploadSize) {
            handleSnackbar("FileTooLarge", snackbarSeverity.ERROR);
          } else if (file && !supportedFileTypes.includes(file.type)) {
            handleSnackbar("UnsupportedFileType", snackbarSeverity.ERROR);
          } else {
            handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
          }
        }}
        acceptedFiles={supportedFileTypes}
        dropzoneParagraphClass="dropZoneText"
        dropzoneClass="custom-dropzone-root"
        fileObjects={selectedFile}
        onChange={(newFiles) => handleSelectedFile(newFiles)}
        dropzoneText={t("DragAndDropAFile")}
        filesLimit={numberOfFileLimist}
        maxFileSize={maxFileUploadSize}
        showAlerts={false}
        clearOnUnmount={true}
        showPreviewsInDropzone={false}
      />

      {selectedFile.length > 0 && (
        <Box>
          {selectedFile.map((file, index) => (
            <Chip
              key={index}
              label={file.name}
              onDelete={() => handleDeleteFromSelectedFiles(file)}
              sx={{
                margin: "5px",
                p: 1,
                justifyContent: "start",
              }}
            />
          ))}
        </Box>
      )}
      <Box>
        <ConfirmAndCancelButtons
          useCustomLoadingButtonOnConfirm={true}
          useCustomLoadingButtonOnCancel={true}
          confirmTitle="Next"
          cancelTitle="Close"
          onConfirm={handleUpload}
          onCancel={() => handleClose()}
          isConfirmLoading={false}
          isCancelLoading={false}
          confirmButtonType="submit"
        />
      </Box>
    </Stack>
  );
}
